import { useState, useEffect } from 'react';
import { useUIStore } from '../store';
import type { RestaurantConfig } from '../ui/uiConfig';
import type { RestaurantPublicDTO } from '../dto/restaurant.dto';
import { fallbackConfig } from '../fallback/fallbackConfig';
import { normalizeColors } from '../utils/normailizeColors';
import { safe } from '../utils/mergeConfig';

const THEME_STORAGE_KEY = 'restaurant-theme';
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

export const useRestaurantConfig = (
  restData: RestaurantPublicDTO | null,
  assetsVersionMs: string
): RestaurantConfig => {
  const { setConfig: setConfigStore, setTheme } = useUIStore();
  const [config, setConfig] = useState<RestaurantConfig>(fallbackConfig);

  useEffect(() => {
    if (!restData?.id) return;

    const favicon =
      safe(restData.favicon_url) ||
      (SUPABASE_URL
        ? `${SUPABASE_URL}/storage/v1/object/public/product-images/${restData.id}/favicon.webp`
        : '');

    const newConfig: RestaurantConfig = {
      ...fallbackConfig,
      favicon,
      id: restData.id,
      title: restData.name,
      logo: restData.logo_url ?? fallbackConfig.logo,
      placeholder_url: restData.placeholder_url ?? fallbackConfig.placeholder_url,
      imagesVersion: assetsVersionMs,
      phoneNumber: safe(restData.phone_number) || fallbackConfig.phoneNumber,
      colors: normalizeColors(restData.colors_json, fallbackConfig.colors),
      header_image_url: restData.header_image_url ?? null,
      address: restData.address ?? null,
      opening_hours: restData.opening_hours ?? null,
      card_info: restData.card_info ?? null,
    };

    setConfig(newConfig);
    setConfigStore(newConfig);

    const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    setTheme(
      storedTheme === 'light' || storedTheme === 'dark'
        ? storedTheme
        : (newConfig.colors.defaultTheme ?? 'light')
    );
  }, [
    restData?.id,
    restData?.name,
    restData?.logo_url,
    restData?.placeholder_url,
    restData?.favicon_url,
    restData?.colors_json,
    assetsVersionMs,
    setConfigStore,
    setTheme,
  ]);

  return config;
};
